// @ts-strict-ignore
import moment, {Moment, MomentInput, unitOfTime} from 'moment';
// which can fail type checking if the date util mixes these with moment-timezone instead
import momentTimezone from 'moment-timezone';
import mapValues from 'lodash/mapValues';
import round from 'lodash/round';
import isString from 'lodash/isString';

import {reporter} from 'common/report/error-reporting';
import {
  CALENDAR_DATE_FORMAT,
  DAYS_OF_THE_WEEK,
  MINUTE_BEFORE_MIDNIGHT_TIME_STRING,
  NO_YEAR_FORMAT,
  SHORT_MONTH_CALENDAR_DATE_FORMAT,
  SHORT_MONTH_NO_YEAR_CALENDAR_DATE_FORMAT,
  TIME_12H_LOWER_TRUNCATED_FORMAT,
  TIMESTAMP_FORMAT,
  Timezone,
  USER_INPUT_TIME_FORMATS,
} from 'common/constants';
import {TimePeriodSettings} from 'common/report/stores/types';
import {RelativeDate, StandardTimeUnit, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {SpecialTimeUnit} from 'common/types/reports/bookmark';

const MS_IN_MINUTE = 60 * 1000;
const MS_IN_HOUR = MS_IN_MINUTE * 60;
const MS_IN_DAY = MS_IN_HOUR * 24;
export const MS_BY_UNIT = {
  second: 1000,
  minute: MS_IN_MINUTE,
  hour: MS_IN_HOUR,
  day: MS_IN_DAY,
  week: MS_IN_DAY * 7,
  month: MS_IN_DAY * 30,
  quarter: MS_IN_DAY * 90,
  year: MS_IN_DAY * 365,
};

// Type guard till we can figure out better pattern to make MS_BY_UNIT interoperable with TimeUnit
const isMsByUnitType = (unit: any): unit is keyof typeof MS_BY_UNIT => {
  return Object.keys(MS_BY_UNIT).includes(unit);
};

export const getMsByTimeUnit = (unit: TimeUnit | keyof typeof TimeUnit): number => {
  if (!isMsByUnitType(unit)) {
    console.warn(`getMsByUnit called with unsupported unit: ${unit}. Defaulting to 1 ms.`);
    return 1;
  }
  return MS_BY_UNIT[unit];
};

const enLocalData = {
  relativeTime: {
    future: `in %s`,
    past: `%s ago`,
    s: `less than a minute`,
    ss: `%d seconds`,
    m: `1 minute`,
    mm: `%d minutes`,
    h: `1 hour`,
    hh: `%d hours`,
    d: `1 day`,
    dd: `%d days`,
    M: `1 month`,
    MM: `%d months`,
    y: `1 year`,
    yy: `%d years`,
  },
};

/**
 * moment text rendering thresholds must be defined via a global config
 */
moment.relativeTimeThreshold(`m`, 60);

/**
 * Setup moment's relative time locales
 */
moment.updateLocale(`en`, enLocalData);
// moment-timezone has its own static global
momentTimezone.updateLocale(`en`, enLocalData);

export const SECONDS_BY_UNIT = mapValues(MS_BY_UNIT, (val) => val / 1000);

export const UNITS = {
  second: `second`,
  minute: `minute`,
  hour: `hour`,
  day: `day`,
  week: `week`,
  month: `month`,
  quarter: `quarter`,
  year: `year`,
};

const SHORT_UNITS = {
  second: `s`,
  minute: `m`,
  hour: `h`,
  day: `D`,
  week: `W`,
  month: `M`,
  quarter: `Q`,
  year: `Y`,
};

const UNITS_ARRAY = [
  UNITS.second,
  UNITS.minute,
  UNITS.hour,
  UNITS.day,
  UNITS.week,
  UNITS.month,
  UNITS.quarter,
  UNITS.year,
];

export const SUB_DAY_TIME_UNITS: TimeUnit[] = [TimeUnit.Minute, TimeUnit.Hour, TimeUnit.Second];

const PARSE_DATE_FORMATS = [
  `M D YY`,
  `M D YYYY`,
  `YY M D`,
  `YYYY M D`,
  `YYYY M DTHH mm ss`,
  `MMM D YY`,
  `MMM Do YY`,
  `MMM D YYYY`,
  `MMM Do YYYY`,
  `MMM D YYYY h mmA`,
  `M D`,
  `M YY`,
  `M YYYY`,
  `MMM D`,
  `MMM Do`,
  `MMM YY`,
  `MMMM D`,
  `MMMM YY`,
  `MMM YYYY`,
  `MMMM YYYY`,
  `MMMM D YYYY`,
  `MMMM Do YYYY`,
  `MMMM D YY`,
  `MMMM Do YY`,
  `MMMM D`,
  `MMMM Do`,
  // Insights api related formats
  // Parse hour format (e.g. "12pm Tue Sep 19")
  `ha ddd MMM D`,
  `ha MMM D YYYY`,
  `ha MMM D`,
  `h mma MMM D YYYY`,
  `MMM D YYYY H mm a`,
  // Parse week range (e.g. "Jan 02 Jan 08")
  `MMM D MMM D`,
  `MMM D MMM D YYYY`,
];

const DISPLAY_DATE_FORMATS = {
  [UNITS.second]: `ddd MMM D, YYYY h:mm:ssA`,
  [UNITS.minute]: `ddd MMM D, YYYY h:mmA`,
  [UNITS.hour]: `ddd MMM D, YYYY h:mmA`,
  [UNITS.day]: `ddd MMM D, YYYY`,
  [UNITS.week]: `MMM D, YYYY`,
  [UNITS.month]: `MMM YYYY`,
  [UNITS.quarter]: `[Q]Q YYYY`,
  [UNITS.year]: `YYYY`,
};

export const MINUTE_BEFORE_MIDNIGHT_TIME: moment.Moment = parseTime(MINUTE_BEFORE_MIDNIGHT_TIME_STRING);
export const ZERO_TIME: moment.Moment = parseTime(`00:00:00`);

export type AbsoluteDateRange = {
  fromDate: string;
  toDate: string;
};

export enum UnitAlignmentBehavior {
  Left = `left`,
  Right = `right`,
  None = `none`,
}

/**
 * @public  Zero indexed. Starts on January (0) and end of December (11)
 * This tag is so knip doesn't flag months currently not in use.
 */
export enum SettingMonth {
  January = 0,
  February,
  March,
  April,
  May,
  June,
  July,
  August,
  September,
  October,
  November,
  December,
}

// Starts on Monday (0) and ends on Sunday (6)
export enum SettingWeekday {
  Monday = 0,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
  Sunday,
}
// Starts on Monday (1) and ends on Sunday (7)
export enum IsoWeekday {
  Monday = 1,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
  Sunday,
}
export function isoWeekdayFromSettingWeekday(weekday: SettingWeekday): IsoWeekday {
  const key: string = SettingWeekday[weekday];
  return IsoWeekday[key as keyof typeof IsoWeekday];
}

export const DEFAULT_WEEKEND_START_DAY = SettingWeekday.Saturday;
export const DEFAULT_WEEKEND_DURATION = 2; // in days
export const DEFAULT_TIME_PERIOD_SETTINGS: TimePeriodSettings = {
  weekStartDay: SettingWeekday.Monday,
  quarterStartMonth: SettingMonth.January,
  weekendStartDay: DEFAULT_WEEKEND_START_DAY,
  weekendDuration: DEFAULT_WEEKEND_DURATION,
} as TimePeriodSettings;

export function getCurrentDate() {
  return new Date();
}

type GetCurrentMomentArgType = {
  localized?: boolean;
  timezone?: Timezone | ``;
};

/**
 * @deprecated use `getCurrentMomentAsOf` instead.
 */
export function getCurrentMoment({localized = false, timezone = ``}: GetCurrentMomentArgType = {}) {
  if (timezone) {
    return momentTimezone.tz(timezone);
  } else if (localized) {
    return moment(getCurrentDate());
  }
  return moment.utc(getCurrentDate());
}

export function getCurrentMomentAsOf(
  projectAsOfDate: Moment | string | Date | null | undefined,
  timezone?: Timezone,
): Moment {
  if (timezone) {
    return projectAsOfDate
      ? (momentTimezone.tz(projectAsOfDate, timezone) as Moment)
      : (momentTimezone().tz(timezone) as Moment);
  }
  if (typeof projectAsOfDate === `string`) {
    return moment(parseDate(projectAsOfDate));
  }
  return projectAsOfDate ? moment(projectAsOfDate) : moment();
}

export function getLocalTimezone(): Timezone {
  return momentTimezone.tz.guess() as Timezone;
}

function shortenUnit(unit) {
  return unit !== `` ? SHORT_UNITS[unit] : unit;
}

/**
 * Takes in a time duration returns an approximate human readable dict.
 * Use @see {@link formatPreciseDuration} to obtain the precise duration.
 *
 * @param {number} duration - time duration in milliseconds
 * @param {number} precision - number of decimal points to round to
 * @param {bool} concise - if a concise unit must be returned. (`w` vs `weeks`)
 * @return {{unit: string, value: number}} time unit and numeric value of the interval
 *
 * @example formatDuration(120 * 60000) returns {unit: `hour`, value: 2}
 * @example formatDuration(121 * 60000) returns {unit: `hour`, value: 2}
 */
export function formatDuration(
  duration: number,
  precision: number,
  concise: boolean = false,
): {unit: string; value: number} {
  const numYears = round(duration / MS_BY_UNIT.year, precision);
  const numMonths = round(duration / MS_BY_UNIT.month, precision);
  const numDays = round(duration / MS_BY_UNIT.day, precision);
  const numHours = round(duration / MS_BY_UNIT.hour, precision);
  const numMinutes = round(duration / MS_BY_UNIT.minute, precision);
  const numSeconds =
    duration >= 1000 ? Math.floor(duration / MS_BY_UNIT.second) : round(duration / MS_BY_UNIT.second, precision);

  let formattedTime = {unit: ``, value: 0};
  if (duration === 0) {
    formattedTime = {unit: UNITS.second, value: 0};
  } else if (0 <= numSeconds && numSeconds < 60) {
    formattedTime = {unit: UNITS.second, value: numSeconds};
  } else if (1 <= numMinutes && numMinutes < 60) {
    formattedTime = {unit: UNITS.minute, value: numMinutes};
  } else if (1 <= numHours && numHours < 24) {
    formattedTime = {unit: UNITS.hour, value: numHours};
  } else if (1 <= numDays && numDays < 90) {
    formattedTime = {unit: UNITS.day, value: numDays};
  } else if (1 <= numMonths && numMonths < 12) {
    formattedTime = {unit: UNITS.month, value: numMonths};
  } else if (1 <= numYears) {
    formattedTime = {unit: UNITS.year, value: numYears};
  }

  if (concise) {
    formattedTime.unit = shortenUnit(formattedTime.unit);
  }
  return formattedTime;
}

/**
 * Takes in a time duration in milliseconds returns a precise human readable dict.
 * Use @see {@link formatDuration} when an approximation is sufficient.
 *
 * @param duration {number} - time duration in milliseconds
 * @return {{unit: string, value: number}} time unit and numeric value of the interval
 *
 * @example formatPreciseDuration(120 * 60000) returns {unit: `hour`, value: 2}
 * @example formatPreciseDuration(121 * 60000) returns {unit: `minute`, value: 121}
 */
export function formatPreciseDuration(duration: number): {unit: keyof typeof MS_BY_UNIT; value: number} {
  if (duration < MS_BY_UNIT.second) return {unit: `second`, value: 0};

  const numYears = Math.floor(duration / MS_BY_UNIT.year);
  const numWeeks = Math.floor(duration / MS_BY_UNIT.week);
  const numDays = Math.floor(duration / MS_BY_UNIT.day);
  const numHours = Math.floor(duration / MS_BY_UNIT.hour);
  const numMinutes = Math.floor(duration / MS_BY_UNIT.minute);
  const numSeconds = Math.floor(duration / MS_BY_UNIT.second);

  let formattedTime: {unit: keyof typeof MS_BY_UNIT; value: number} = {unit: `minute`, value: 0};
  if (duration % MS_BY_UNIT.year === 0 && numYears >= 1) {
    formattedTime = {unit: `year`, value: numYears};
  } else if (duration % MS_BY_UNIT.week === 0 && numWeeks >= 1) {
    formattedTime = {unit: `week`, value: numWeeks};
  } else if (duration % MS_BY_UNIT.day === 0 && numDays >= 1) {
    formattedTime = {unit: `day`, value: numDays};
  } else if (duration % MS_BY_UNIT.hour === 0 && numHours >= 1) {
    formattedTime = {unit: `hour`, value: numHours};
  } else if (duration % MS_BY_UNIT.minute === 0 && numMinutes >= 1) {
    formattedTime = {unit: `minute`, value: numMinutes};
  } else if (duration % MS_BY_UNIT.second === 0 && numSeconds >= 1) {
    formattedTime = {unit: `second`, value: numSeconds};
  }
  return formattedTime;
}

/**
 * Formats a single duration in concise format for annotating median.
 * @param {number | undefined} duration in seconds
 * @return {string} formatted string describing the median
 */
export function formatConciseDuration(duration: number | undefined, precision = 1): string {
  if (duration === undefined) {
    reporter.error(`duration ${duration} is not valid`);
    return ``;
  }
  precision = duration < 1 ? 3 : precision; // if duration is less than 1 second, use 3 decimal points
  const {unit: u, value: v} = formatDuration(duration * 1000, precision, true);
  return `${v}${u}`;
}

function preProcessDateString(dateString: string): string {
  // recent versions of moment return moment.invalid when day of week doesn't match date
  // (e.g. "Tue Dec 1" in 2018 when Dec 1 is Sat). In order to preserve current Insights
  // behavior (from moment 2.17.1) we strip out day of week (matching format string "ddd").
  dateString = dateString.replace(/(mon|tue|wed|thu|fri|sat|sun),?/gi, ``).trim();
  // replace all chars other than alphanumerics and spaces with a space
  // and replace multiple spaces with a single space
  // this allows strings like '"2017-02-02"' to be parsed correctly in strict mode
  dateString = dateString.replace(/\W+/g, ` `);
  return dateString;
}

function parseToMostRecentPast(momentDate: Moment): Nullable<Moment> {
  // very weird handling here:
  // assume today is 2025-08-05, if we got '12/31' then it must means last year....
  // so we subtract 1 year from the date
  momentDate = momentDate > moment().endOf(`day`) ? momentDate.subtract(1, `years`) : momentDate;
  return momentDate;
}

export function clampDateRange({
  from,
  to,
  limit,
  format,
}: {
  from: MomentInput;
  to: MomentInput;
  limit: [MomentInput, MomentInput];
  format?: string;
}): {from: Moment; to: Moment} {
  const [min, max] = sortDates(limit.map((l) => moment(l, format)));
  from = moment(from);
  to = moment(to);
  if (from.isBefore(min)) {
    from = min;
  }
  if (from.isAfter(max)) {
    from = max;
  }
  if (to.isBefore(min)) {
    to = min;
  }
  if (to.isAfter(max)) {
    to = max;
  }
  return {from, to};
}

/** Formats the given date range in day granularity.
 *
 * Do not use this if you need sub-day granularity, like the time of day.
 *
 * @example `formatDateRange({from: '2021-12-01', to: '2021-12-31'})` returns 'Dec 1 - Dec 31 2021'
 * @example `formatDateRange({from: '2021-12-01', to: '2022-01-31'})` returns 'Dec 1 2021 - Jan 31 2022'
 */
function formatDateRange({from, to}: {from: MomentInput; to: MomentInput}) {
  const mFrom = moment(from);
  const mTo = moment(to);
  const fromStr = mFrom.format(
    mFrom.year() !== mTo.year() ? SHORT_MONTH_CALENDAR_DATE_FORMAT : SHORT_MONTH_NO_YEAR_CALENDAR_DATE_FORMAT,
  );
  const toStr = mTo.format(SHORT_MONTH_CALENDAR_DATE_FORMAT);
  /** Dec 5 - Dec 11 2021 */
  return `${fromStr} - ${toStr}`;
}

function formatWeekRange(mDate: MomentInput) {
  const mToDate = moment(mDate).add(6, `days`);
  return formatDateRange({from: mDate, to: mToDate});
}

export type ParseDateOptions = Partial<{
  /** treat input date string as an ISO-formatted UTC date (i.e. "2017-02-28") */
  iso: boolean;
  /** set date time to start of day */
  alignToStartOfDay: boolean;
  /** set date time to end of day */
  alignToEndOfDay: boolean;
}>;

/**
 * Take two moments and set the first moment's (`date`) time component to the second moment's
 * (`dateWithTime`) time component. The result will be a moment with the date taken from `date`
 * and the time taken from `dateWithTime`.
 *
 * @param date the moment to take the date from, this must not be null.
 * @param dateWithTime the moment to take the time from. If this is null `ZERO_TIME` (00:00:00)
 *          will be used for time instead.
 * @param defaultTime the moment to take time from if dateWithTime is null or undefined.
 * @returns a moment with the date from `date` and the time from `dateWithTime`.
 */
export function combineDateWithTimeFromDate(
  date: moment.Moment,
  dateWithTime: moment.Moment,
  defaultTime: moment.Moment = ZERO_TIME,
): moment.Moment {
  const time =
    dateWithTime !== null && dateWithTime !== undefined && dateWithTime.isValid() ? getTime(dateWithTime) : defaultTime;
  const timeDuration = moment.duration({
    hours: time.hours(),
    minutes: time.minutes(),
  });
  return moment(date).startOf(`day`).add(timeDuration);
}

/**
 * Parse a date string into a Date object (sets date time to start of day)
 * Strings that parse to a future date (i.e. "12/31") are moved to the most recent *past* instance
 * of that date.
 *
 * @param {string} dateString - string to parse
 * @param {Object} [config] - object of optional configuration values
 * @param {boolean} [config.iso=false] - treat input date string as an ISO-formatted UTC date (i.e. "2017-02-28")
 * @param {boolean} [config.alignToStartOfDay=false] - set date time to start of day
 * @param {boolean} [config.alignToEndOfDay=false] - set date time to end of day
 * @returns {?Date} - the Date object the input string parses to, or null if input is invalid
 */
export function parseDate(
  dateString: string,
  {iso = false, alignToStartOfDay = false, alignToEndOfDay = false}: ParseDateOptions = {},
): Nullable<Date> {
  let date: Nullable<Date> = null;
  let mDate: moment.Moment;

  if (typeof dateString === `string`) {
    if (iso) {
      mDate = moment.utc(new Date(dateString));
    } else {
      dateString = preProcessDateString(dateString);
      mDate = moment(dateString, PARSE_DATE_FORMATS, /* strict */ true);
    }

    if (mDate.isValid()) {
      mDate = alignToStartOfDay ? mDate.startOf(UNITS.day as unitOfTime.StartOf) : mDate;
      mDate = alignToEndOfDay ? mDate.endOf(UNITS.day as unitOfTime.StartOf) : mDate;

      // make dates like "12/31" parse to the most recent past instance of that date
      // use "strict parsing" mode of moment to check whether dateString is of the form M/D, MM/DD,
      // M.D, etc.
      if (moment(dateString, NO_YEAR_FORMAT, true).isValid()) {
        mDate = parseToMostRecentPast(mDate);
      }

      date = mDate.toDate();
    }
  }

  return date;
}

/**
 * Parses dateString which is in one of the formats PARSE_DATE_FORMATS. It is important to note that
 * dateString MUST be parsed with a correct timezone.
 *
 * For example, 2022-10-01T00:00:00 America/Los_Angeles is different
 * from 2022-10-01T00:00:00 Australia/Melbourne.
 *
 * In order to parse a given string in a specified timezone, we use MomentTimezone. We use strict
 * parsing mode for performance reasons. Strings that parse to a future date (i.e. "12/31") are
 * moved to the most recent *past* instance
 * of that date.
 *
 * @param {string} dateString - string to parse
 * @param {Timezone} timezone  - timezone to be used when parsing dateString
 * @param {Object} [config] - object of optional configuration values
 * @param {boolean} [config.alignToStartOfDay=false] - set date time to start of day
 * @param {boolean} [config.alignToEndOfDay=false] - set date time to end of day
 * @returns {?Moment} - timezone aware Moment object the input string parses to, or null if input is invalid
 */
export function parseDateWithTimeZone(
  dateString: string,
  timezone: Timezone,
  {alignToStartOfDay = false, alignToEndOfDay = false}: ParseDateOptions = {},
): Nullable<Moment> {
  let mDate;

  if (typeof dateString === `string`) {
    dateString = preProcessDateString(dateString);
    mDate = momentTimezone.tz(dateString, PARSE_DATE_FORMATS, /* strict */ true, timezone);
  }

  if (mDate.isValid()) {
    mDate = alignToStartOfDay ? mDate.startOf(UNITS.day) : mDate;
    mDate = alignToEndOfDay ? mDate.endOf(UNITS.day) : mDate;

    // make dates like "12/31" parse to the most recent past instance of that date
    // use "strict parsing" mode of moment to check whether dateString is of the form M/D, MM/DD,
    // M.D, etc.
    if (moment(dateString, NO_YEAR_FORMAT, true).isValid()) {
      mDate = parseToMostRecentPast(mDate);
    }
  }
  return mDate;
}

/**
 * Converts the passed in moment to a moment holding only the time from the original moment with
 * hour/minute/second precision.
 *
 * @param {Moment} timestamp the moment to take the time from.
 * @returns {Moment} the moment with only the time.
 */
export function getTime(timestamp: moment.Moment): moment.Moment {
  return moment({hour: timestamp.hour(), minute: timestamp.minute(), second: timestamp.second()});
}

/**
 * Parses the given time string and formats it into the moment format: `h:mm a`.
 *
 * @param {string} time string in a supported time format, see USER_INPUT_TIME_FORMATS for details.
 * @returns {string} the string representation of the time from the passed in moment in the format
 *            `h:mm a`.
 */
export function formatTimeString(time: string): string {
  return getTimeString(parseTime(time));
}

/**
 * Converts the passed in moment to a string representation of the time from the moment. Any date
 * component is ignored. The time string will be in 12h format with am/pm, specifically the moment
 * format: `h:mm a`.
 *
 * @param {Moment} timestamp the moment to take the time from.
 * @returns {string} the string representation of the time from the passed in moment in the format
 *            `h:mm a`.
 */
export function getTimeString(timestamp: moment.Moment): string {
  return getTime(timestamp).format(TIME_12H_LOWER_TRUNCATED_FORMAT);
}

/**
 * Parses the given string into a time-only moment. Expects a string in the format `h:mm:ss A`,
 * `h:mm A`, `hh:mm:ss A`, `hh:mm A`, `hh:mm:ss a`, `hh:mm a`, `HH:mm:ss`, or`HH:mm`.
 *
 * @param {string} time string in the format `h:mm:ss A`, `h:mm A`, `hh:mm:ss A`, `hh:mm A`,
 *                  `hh:mm:ss a`, `hh:mm a`, `HH:mm:ss`, or`HH:mm`.
 * @returns {Moment} a moment holding the time.
 */
export function parseTime(time: string): moment.Moment {
  return moment(time, USER_INPUT_TIME_FORMATS, true);
}

/**
 * Compares the times in the two moments and returns true if the times are the same, false
 * otherwise. Compares hour, minute, and second.
 *
 * @param {Moment} moment1 first moment.
 * @param {Moment} moment2 second moment.
 * @returns {boolean} true if the hour, minute, and second of the two moments are equal.
 */
function isSameTime(moment1: moment.Moment, moment2: moment.Moment): boolean {
  return (
    moment1.hour() === moment2.hour() && moment1.minute() === moment2.minute() && moment1.second() === moment2.second()
  );
}

/**
 * Checks if the given moment's time is all zeros in hour, minute, and second, i.e. 00:00:00.
 *
 * @param {Moment} momentTime the moment to check.
 * @returns true if the time is 00:00:00 and false otherwise.
 */
export function isTimeZero(momentTime: moment.Moment): boolean {
  return isSameTime(getTime(momentTime), ZERO_TIME);
}

/**
 * Parses the given string into a time-only moment. Expects a string in the format
 * `YYYY-MM-DDTHH:mm:ss`.
 *
 * @param {string} timestamp the time string in the format `YYYY-MM-DDTHH:mm:ss`.
 * @returns {Moment} a moment holding the time.
 */
export function parseTimestamp(timestamp: string): moment.Moment {
  return moment(timestamp, TIMESTAMP_FORMAT);
}

type FormatDateOptions = Partial<{
  customFormatting: Record<string, string>;
  displayRangeIfWeek: boolean;
  iso: boolean;
  timezone: Nullable<string>;
  hideTimezone?: boolean;
  unit: Nullable<string>; // TODO: Change to TimeUnit
  utc: boolean;
  weekRangeFormatter: (mDate: Moment) => string;
  quarterStartMonth?: number;
}>;
/**
 * Convert a Date object or integer timestamp into a formatted date string.
 *
 * @param {Date|number} date - Date object or timestamp to format
 * @param {Object} [config] - object of optional configuration values
 * @param {boolean} [config.iso=false] - format date to ISO-style string (YYYY-MM-DD)
 * @param {boolean} [config.utc=false] - treat input as a UTC date
 * @param {string|null} [config.unit=null] - use the corresponding format for this unit from DISPLAY_DATE_FORMATS
 * @param {Object} [config.customFormatting={}] - map of units to custom format strings to be used in place of DISPLAY_DATE_FORMATS
 * @param {boolean} [config.displayRangeIfWeek=true] - if unit is week format to range string across week (i.e. "Dec 1 - Dec 7")
 * @param {Function} [config.weekRangeFormatter] - function for formatting weekly ranges
 * @returns {?string} - the string the input Date object formats to, or null if input is invalid
 */
export function formatDate(
  date: Date | number | Moment | string | null,
  {
    iso = false,
    utc = false,
    unit = null,
    customFormatting = {},
    displayRangeIfWeek = true,
    weekRangeFormatter = formatWeekRange,
    timezone = null,
    hideTimezone = false,
    quarterStartMonth,
  }: FormatDateOptions = {},
): Nullable<string> {
  let dateString: Nullable<string> = null;
  let mDate: Moment;

  if ((date instanceof Date || Number.isInteger(date) || moment.isMoment(date) || typeof date === `string`) && !!date) {
    mDate = utc ? moment.utc(Number(date)) : moment(Number(date));
    if (timezone) {
      mDate = momentTimezone(Number(date)).tz(timezone);
    }

    if (mDate.isValid()) {
      if (iso) {
        dateString = mDate.format().slice(0, 10);
      } else if (unit === UNITS.quarter && typeof quarterStartMonth === `number` && !customFormatting[UNITS.quarter]) {
        dateString = formatFiscalQuarter(mDate, quarterStartMonth);
      } else {
        if (displayRangeIfWeek && unit === UNITS.week) {
          dateString = weekRangeFormatter(mDate);
        } else {
          let format: Nullable<string> = null;
          if (unit) {
            format = customFormatting[unit] || DISPLAY_DATE_FORMATS[unit];
            // Add timezone to the format string if it's sub-day.
            if (!hideTimezone && timezone && [UNITS.hour, UNITS.minute, UNITS.second].includes(unit)) {
              format += ` z`;
            }
          }
          dateString = mDate.format(format || SHORT_MONTH_CALENDAR_DATE_FORMAT);
        }
      }
    }
  }

  return dateString;
}

/**
 * Format a moment as a fiscal quarter label (e.g. "Q1 2026") using the project's
 * quarter-start month. Naming follows the same convention as `parseQuarter` /
 * `startOfQuarter`: the year is the calendar year of the fiscal quarter's first month.
 */
function formatFiscalQuarter(mDate: Moment, quarterStartMonth: number): string {
  const q = ((quarterStartMonth % 3) + 3) % 3;
  const month = mDate.month();
  const fiscalMonthIndex = (month - q + 12) % 12;
  const quarterNum = Math.floor(fiscalMonthIndex / 3) + 1;
  const year = month < q ? mDate.year() - 1 : mDate.year();
  return `Q${quarterNum} ${year}`;
}

export function parseAndFormatDate(date) {
  return formatDate(parseDate(date));
}

type LocalizedDateOptions = Partial<{utcOffset: Nullable<number>; date: Nullable<Date>}>;
/**
 * Returns the date provided in project localized time. If no date specificed use now.
 * This was largely taken from analytics/media/js/utility.js
 *
 * @param {Object} [config] - object of optional configuration values
 * @param {integer|null} [config.utcOffset=null] - config.the offset of UTC time in hours
 * @param {Date|null} [config.date=null] - a Date object to be offset. if this is not provided the
 *    function will create a new Date
 * @returns {Date} - a Date in "project time" according to the utcOffset provided
 */
export function localizedDate({utcOffset = 0, date = null}: LocalizedDateOptions = {}): Date {
  date = date instanceof Date ? date : new Date();
  date = new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    date.getUTCMilliseconds(),
  );
  if (utcOffset && Number.isInteger(utcOffset)) {
    date.setMinutes(date.getMinutes() + utcOffset);
  }
  return date;
}

/**
 * Convert a "relative date" integer (i.e. 5 [days ago]) and a unit into an "absolute" Date object.
 *
 * @param {number} relativeDateInt - integer that (along with unit) represents a time offset from
 * the current date
 * @param {string} unit - specifies the unit of time to multiply the relativeDateInt by to
 * calculate the absolute date
 * @param {Object} [config] - object of optional configuration values
 * @param {integer|null} [config.utcOffset=null] - the offset of UTC time in hours
 * @returns {?Date} - the Date object the given relative date integer and unit resolve to, or null
 *    if input is invalid
 */
export function relativeToAbsoluteDate(relativeDateInt: number, unit: string, {utcOffset = null} = {}): Nullable<Date> {
  let date: Nullable<Date> = null;
  const now = utcOffset === null ? moment() : moment(localizedDate({utcOffset}));

  if (Number.isInteger(relativeDateInt) && UNITS_ARRAY.includes(unit)) {
    date = now.subtract(relativeDateInt, `${unit}s` as unitOfTime.DurationConstructor).toDate();
  }

  return date;
}

/**
 * Accepts a (from, to) date range and the focused date ('to' or 'from') being edited. Normalizes the date range
 * if from > to:
 * 1) if focusedDate is `from`, sets to = from
 * 2) if focusedDate is `to`, sets from = to
 *
 * @param {string} from - from date
 * @param {string} to - to date
 * @param {string} focusedDate - the date being edited resulting in the current date range; one of <`from`, `to`>
 * @returns {Array.<string>} - the normalized date range [from, to]
 */
export function normalizeDateRange(from: string, to: string, focusedDate: `from` | `to`): [string, string] {
  if (!from || !to) {
    return [from, to];
  }

  const parsedFrom = parseDate(from) ?? 0;
  const parsedTo = parseDate(to) ?? 0;

  if (focusedDate === `from`) {
    if (parsedFrom > parsedTo) {
      return [from, from];
    }
  } else if (focusedDate === `to` && parsedTo < parsedFrom) {
    return [to, to];
  }

  return [from, to];
}

/**
 * Accepts a list of date strings and
 * - ensures none of them exceed the current moment (unless allowFuture=true is specified)
 * - sorts them in ascending order
 * - formats them as ISO date strings (YYYY-MM-DD)
 *
 * @param {Array.<string>} dateStrings - list of date strings to normalize, can be any format that parseDate accepts
 * @param {Object} [config] - object of optional configuration values
 * @param {integer|null} [config.utcOffset=null] - the offset of UTC time in hours
 * @param {boolean} [config.allowFuture=false] - if true, does not normalize future dates to the current moment
 * @returns {Array.<?string>} - list of ISO-formatted normalized date strings, in ascending order
 */
export function normalizeDateStrings(dateStrings: string[] = [], {utcOffset = null, allowFuture = false} = {}) {
  let dates = dateStrings.reduce((accumDates, date) => {
    const parsed = parseDate(date);
    if (parsed) {
      accumDates.push(parsed);
    }
    return accumDates;
  }, [] as Date[]);

  if (!allowFuture) {
    const now = utcOffset === null ? new Date() : localizedDate({utcOffset});
    dates = dates.map((date) => (date > now ? now : date));
  }

  return dates
    .map((date) => date.getTime())
    .sort((a, b) => a - b)
    .map((date) => formatDate(date, {iso: true}));
}

/**
 * If DST is active on given date, will generally return 60 (minutes), though there are timezones
 * that use 30 minute DST offset (e.g. Australia/Lord_Howe).
 */
function getDstOffset(momentDate) {
  const nonDstOffset = momentTimezone(momentDate).month(0).utcOffset(); // use a month in the winter (no DST)
  return momentDate.utcOffset() - nonDstOffset; // amount momentDate is offset due to DST
}

/**
 * Offset times by the diff between project and local timezone to trick Highcharts into displaying
 * data in project time.
 */
export function offsetForHighcharts(momentDate, projectTimezone, projectOffset): Moment {
  // Correct for DST - projectOffset changes depending on whether DST is active at the current moment in project time.
  // This doesn't work for us since local time may have different DST rules, so correct the offsets accordingly.
  projectOffset = projectOffset - getDstOffset(momentTimezone.tz(projectTimezone));
  const localOffset = momentDate.utcOffset() - getDstOffset(momentTimezone.tz(momentDate, projectTimezone));
  return moment(momentDate).add(projectOffset - localOffset, `minutes`);
}

/**
 * Add or subtract months with projectTz awareness.
 */
export function addMonths(date: Date | Moment | string, numMonths: number, projectTz: Timezone): Date {
  return momentTimezone.tz(date, projectTz).add(numMonths, `months`).toDate();
}

/**
 * Convenience helper to check if a date is at the start of the day
 */
export function isAtStartOfDay(date: Date, projectTz: Timezone): boolean {
  return date.getTime() === startOfDay(date, projectTz).getTime();
}

/**
 * Convenience helper to check if a date is at the end of the day
 */
export function isAtEndOfDay(date: Date, projectTz: Timezone): boolean {
  // Because BaseDate has timestamps with second precision, we sometimes lose the millisecond precision
  // To address this, check if the timestamp is greater than or equal to the last second of the day
  const lastSecondOfDay = momentTimezone.tz(date, projectTz).endOf(`day`).startOf(`second`).toDate();
  return date.getTime() >= lastSecondOfDay.getTime();
}

/**
 * Convenience helper to get the start of day with projectTz awareness.
 */
export function startOfDay(date: Date | Moment | string, projectTz: Timezone): Date {
  return momentTimezone.tz(date, projectTz).startOf(`day`).toDate();
}

/**
 * Convenience helper to get the end of day with projectTz awareness.
 */
export function endOfDay(date: Date | Moment | string, projectTz: Timezone): Date {
  return momentTimezone.tz(date, projectTz).endOf(`day`).toDate();
}

/**
 * Get the start of week with projectTz awareness.
 */
export function startOfWeek(date: Date | Moment | string, projectTz: Timezone, isoWeekStartDay: IsoWeekday): Date {
  const dateMoment = momentTimezone.tz(date, projectTz).startOf(`day`);
  let delta = dateMoment.isoWeekday() - isoWeekStartDay;
  if (delta < 0) {
    delta = 7 + delta;
  }
  return dateMoment.subtract(delta, `days`).toDate();
}

/**
 * Get the end of week with projectTz awareness.
 */
export function endOfWeek(date: Date | Moment | string, projectTz: Timezone, isoWeekStartDay: IsoWeekday): Date {
  return momentTimezone(startOfWeek(date, projectTz, isoWeekStartDay))
    .tz(projectTz)
    .add(6, `days`)
    .endOf(`day`)
    .toDate();
}

/**
 * Get the start of month with projectTz awareness.
 */
export function startOfMonth(date: Date | Moment | string, projectTz: Timezone): Date {
  return momentTimezone.tz(date, projectTz).startOf(`month`).toDate();
}

/**
 * Get the end of month with projectTz awareness.
 */
export function endOfMonth(date: Date | Moment | string, projectTz: Timezone): Date {
  return momentTimezone.tz(date, projectTz).endOf(`month`).toDate();
}

/**
 * Get the start of quarter for with projectTz awareness.
 */
export function startOfQuarter(date: Date | Moment | string, projectTz: Timezone, quarterStartMonth: number): Date {
  const dateMoment = momentTimezone.tz(date, projectTz).startOf(`month`);
  let delta = (dateMoment.month() % 3) - (quarterStartMonth % 3);
  if (delta < 0) {
    delta = 3 + delta;
  }
  return dateMoment.subtract(delta, `months`).toDate();
}

/**
 * Get the end of quarter with projectTz awareness.
 */
export function endOfQuarter(date: Date | Moment | string, projectTz: Timezone, quarterStartMonth: number): Date {
  return momentTimezone(startOfQuarter(date, projectTz, quarterStartMonth))
    .tz(projectTz)
    .add(2, `months`)
    .endOf(`month`)
    .toDate();
}

/**
 * Get the start of the fiscal year with projectTz awareness.
 */
export function startOfFiscalYear(
  date: Date | Moment | string,
  projectTz: Timezone,
  fiscalYearStartMonth: number,
): Date {
  const dateMoment = momentTimezone.tz(date, projectTz).startOf(`month`);
  const fiscalYear = fiscalYearStartMonth > dateMoment.month() ? dateMoment.year() - 1 : dateMoment.year();
  return momentTimezone.tz(`${fiscalYearStartMonth + 1}/1/${fiscalYear}`, `M/D/YYYY`, projectTz).toDate();
}

/**
 * Get the end of the fiscal year for a js Date with projectTz awareness.
 */
export function endOfFiscalYear(date: Date | Moment | string, projectTz: Timezone, fiscalYearStartMonth: number): Date {
  const fiscalYearEndMonth = fiscalYearStartMonth === 0 ? 11 : fiscalYearStartMonth - 1;
  const dateMoment = momentTimezone.tz(date, projectTz).startOf(`month`);
  const fiscalYear = fiscalYearEndMonth < dateMoment.month() ? dateMoment.year() + 1 : dateMoment.year();
  return momentTimezone
    .tz(`${fiscalYearEndMonth + 1}/1/${fiscalYear}`, `M/D/YYYY`, projectTz)
    .endOf(`month`)
    .toDate();
}

function sanitizeRelativeDateOffsetForQueryApi(relativeDate: RelativeDate): RelativeDate {
  /**
   * query-api considers 1 month = 30 days and 1 year = 365 days, so we parse dates to make labels, tooltips and other UI elements consistent.
   */
  if (relativeDate.unit === TimeUnit.Month) {
    relativeDate = {
      unit: TimeUnit.Day,
      value: relativeDate.value * 30,
    };
  } else if (relativeDate.unit === TimeUnit.Quarter) {
    relativeDate = {
      unit: TimeUnit.Day,
      value: relativeDate.value * 90,
    };
  } else if (relativeDate.unit === TimeUnit.Year) {
    relativeDate = {
      unit: TimeUnit.Day,
      value: relativeDate.value * 365,
    };
  }

  return relativeDate;
}

/**
 * @deprecated use convertRelativeDateToCalendarDateStrAsOf instead with proper project timezone, timePeriodSettings and asOfDate plumbing.
 *
 * This will use the users local timezone, now as the asOfDate and moment default weekStart and quarterStart, which WILL result in the wrong
 * results being returned in many contexts.
 */
export function convertRelativeDateToCalendarDateStr(relativeDate: RelativeDate | undefined): string | null {
  if (!relativeDate) {
    return null;
  }
  return convertRelativeDateToCalendarDateStrAsOf(
    relativeDate,
    getCurrentDate(),
    getLocalTimezone() as Timezone,
    {weekStartDay: 0, quarterStartMonth: 0} as TimePeriodSettings,
  );
}

export function convertRelativeDateToCalendarDateStrAsOf(
  relativeDate: RelativeDate,
  asOfDate: Date | string | null,
  projectTz: Timezone,
  timePeriodSettings: TimePeriodSettings,
  dateFormat: string = CALENDAR_DATE_FORMAT,
  unitAlignment: UnitAlignmentBehavior = UnitAlignmentBehavior.None,
): string {
  const calendarDate: Date = convertRelativeDateToCalendarDateAsOf(
    relativeDate,
    asOfDate,
    projectTz,
    timePeriodSettings,
    unitAlignment,
  );
  return momentTimezone.tz(calendarDate, projectTz).format(dateFormat || CALENDAR_DATE_FORMAT);
}

export function convertRelativeDateToCalendarDateAsOf(
  relativeDate: RelativeDate,
  asOfDate: Date | string | null,
  projectTz: Timezone,
  timePeriodSettings: TimePeriodSettings,
  unitAlignment: UnitAlignmentBehavior,
): Date {
  if (unitAlignment === UnitAlignmentBehavior.None) {
    relativeDate = sanitizeRelativeDateOffsetForQueryApi(relativeDate);
  }
  assertIsStandardTimeUnit(relativeDate.unit);

  const asOfMoment = asOfDate ? momentTimezone.tz(asOfDate, projectTz) : momentTimezone().tz(projectTz);
  const calendarDate = asOfMoment.subtract(relativeDate.value, relativeDate.unit).toDate();
  if (unitAlignment === UnitAlignmentBehavior.Left) {
    switch (relativeDate.unit) {
      // Regular units
      case TimeUnit.Minute:
      case TimeUnit.Hour:
      case TimeUnit.Day:
      case TimeUnit.Month:
        return momentTimezone
          .tz(calendarDate, projectTz)
          .startOf(String(relativeDate.unit) as unitOfTime.StartOf)
          .toDate();
      // Irregular units
      case TimeUnit.Week:
        return startOfWeek(
          calendarDate,
          projectTz,
          isoWeekdayFromSettingWeekday(timePeriodSettings.weekStartDay as SettingWeekday),
        );
      case TimeUnit.Quarter:
        return startOfQuarter(calendarDate, projectTz, timePeriodSettings.quarterStartMonth);
      case TimeUnit.Year:
        return startOfFiscalYear(calendarDate, projectTz, timePeriodSettings.quarterStartMonth);
    }
  }
  if (unitAlignment === UnitAlignmentBehavior.Right) {
    switch (relativeDate.unit) {
      // Regular units
      case TimeUnit.Minute:
      case TimeUnit.Hour:
      case TimeUnit.Day:
      case TimeUnit.Month:
        return momentTimezone
          .tz(calendarDate, projectTz)
          .endOf(String(relativeDate.unit) as unitOfTime.StartOf)
          .toDate();
      // Irregular units
      case TimeUnit.Week:
        return endOfWeek(
          calendarDate,
          projectTz,
          isoWeekdayFromSettingWeekday(timePeriodSettings.weekStartDay as SettingWeekday),
        );
      case TimeUnit.Quarter:
        return endOfQuarter(calendarDate, projectTz, timePeriodSettings.quarterStartMonth);
      case TimeUnit.Year:
        return endOfFiscalYear(calendarDate, projectTz, timePeriodSettings.quarterStartMonth);
    }
  }

  return calendarDate;
}

/**
 * Non moment util to convert datetime string in UTC to browser timezone
 */
export function convertUTCToLocalized(ds: string) {
  return new Date(ds);
}

// TODO: This might not work properly across DST savings boundaries. Need to verify.
/** Sort a list of ISO date strings.
 *
 * @param {Array.<string>} isoDates - list of iso dates
 * @returns {Array.<string>} sorted dates
 */
export function sortIsoDates(isoDates: string[]): string[] {
  return isoDates.sort();
}

function sortDates<T extends Moment[]>(dates: T): T {
  return dates.sort((a, b) => (a.isBefore(b) ? -1 : 1));
}

/**
 * Returns the ISO 8601 calendar date from a date
 *
 * @param {string|number|Date|Moment} date
 */
export function getCalendarDate(date: string | Date | number | Moment): string {
  return moment.parseZone(date).format(TIMESTAMP_FORMAT);
}

function getMonthStartDate(month: number, year: number) {
  return getCalendarDate(new Date(year, month - 1, 1));
}

function getMonthEndDate(month: number, year: number) {
  return getCalendarDate(new Date(year, month, 0));
}

function parseSecond(dateStr: string, {iso}: {iso: boolean}) {
  const parsedDate = parseDate(dateStr, {iso});
  if (parsedDate) {
    const fromDate = moment(parsedDate).format(TIMESTAMP_FORMAT);
    const toDate = moment(fromDate).add(1, `second`);
    return [fromDate, toDate.format(TIMESTAMP_FORMAT)];
  }
  return null;
}

function parseMinute(dateStr: string) {
  const parsedDate = parseDate(dateStr);
  if (parsedDate) {
    const fromDate = moment(parsedDate).format(TIMESTAMP_FORMAT);
    const toDate = moment(fromDate).add(1, `minute`);
    return [fromDate, toDate.format(TIMESTAMP_FORMAT)];
  }
  return null;
}

function parseHour(dateStr: string) {
  const parsedDate = parseDate(dateStr);
  if (parsedDate) {
    const fromDate = moment(parsedDate).format(TIMESTAMP_FORMAT);
    const toDate = moment(fromDate).add(1, `hour`);
    return [fromDate, toDate.format(TIMESTAMP_FORMAT)];
  }
  return null;
}

function parseDay(day: string | Date) {
  try {
    /** parses days with the following format: Oct 07 '20 */
    return getCalendarDate(new Date(isString(day) ? day.replace(`'`, ``) : day));
  } catch {
    return null;
  }
}

function parseWeek(dateStr: string) {
  if (dateStr.indexOf(` - `) !== -1) {
    const toDateStr = dateStr.split(` - `)[1];
    const toDate = getCalendarDate(toDateStr);
    const fromDate = moment(toDate).subtract(6, `day`);
    if (fromDate && toDate) {
      return [getCalendarDate(fromDate.toDate()), toDate];
    }
  }
  return null;
}

function parseMonth(dateStr: string) {
  const months = [`jan`, `feb`, `mar`, `apr`, `may`, `jun`, `jul`, `aug`, `sep`, `oct`, `nov`, `dec`];

  const monthRegex = new RegExp(`^[a-zA-z]{3} \\d{4}`);
  if (monthRegex.test(dateStr)) {
    const [monthStr, yearStr] = dateStr.split(` `);
    const monthIdx = months.indexOf(monthStr.toLowerCase());
    const yearNum = Number(yearStr);

    if (monthIdx !== -1 && !Number.isNaN(yearNum)) {
      return [getMonthStartDate(monthIdx + 1, yearNum), getMonthEndDate(monthIdx + 1, yearNum)];
    }
  }

  return null;
}

function parseQuarter(dateStr: string, quarterStartMonth: number) {
  const quarterRegex = new RegExp(`^Q[1-4] \\d{4}`);
  if (quarterRegex.test(dateStr)) {
    const [quarterStr, yearStr] = dateStr.split(` `);
    const quarterNum = Number(quarterStr[1]);
    const startMonth = (quarterNum - 1) * 3 + 1 + (quarterStartMonth % 3);
    let endMonth = quarterNum * 3 + (quarterStartMonth % 3);
    let yearNum = Number(yearStr);

    const startDate = getMonthStartDate(startMonth, yearNum);
    if (endMonth > 12) {
      endMonth -= 12;
      yearNum++;
    }
    const endDate = getMonthEndDate(endMonth, yearNum);

    return [startDate, endDate];
  }
  return null;
}

function parseYear(dateStr: string) {
  const yearRegex = new RegExp(`^\\d{4}$`);
  if (yearRegex.test(dateStr)) {
    return [getCalendarDate(dateStr.concat(`-01-01`)), getCalendarDate(dateStr.concat(`-12-31`))];
  }
  return null;
}

/**
 * Parses the data and returns a date range [from, to]
 *
 * @param {string} date - date to parse
 * @param {SpecialTimeUnit} unit - time unit to format the date
 * @returns {?string} - a stringified index that maps to special date
 */
export function parseSpecialDate(date: string, unit: SpecialTimeUnit): string | null {
  if (date === `undefined`) return date;

  switch (unit) {
    case SpecialTimeUnit.HourOfDay:
      return String(Number(date.split(`:`)[0]));
    case SpecialTimeUnit.DayOfWeek:
      return String(DAYS_OF_THE_WEEK.findIndex((day) => day === date));
    default:
      return null;
  }
}

/**
 * Parses the data and returns a date range [from, to]
 *
 * @param {Date|number|string} date - date to parse
 * @param {TimeUnit} unit - time unit to format the date
 * @param {number} number - quarter start month
 * @returns {Array.<string>} - date range [from, to]
 */
export function parseDatetimeRange(date: Date | number | string, unit: TimeUnit, quarterStartMonth?: number) {
  const formattedDate = isString(date) ? date : formatDate(date, {unit});
  if (!formattedDate) return null;
  if (formattedDate === `undefined`) return date;

  switch (unit) {
    // ISO format
    case null:
      return parseSecond(formattedDate, {iso: true});
    case TimeUnit.Minute:
      return parseMinute(formattedDate);
    case TimeUnit.Hour:
      return parseHour(formattedDate);
    case TimeUnit.Day:
      return parseDay(formattedDate);
    case TimeUnit.Week:
      return parseWeek(formattedDate);
    case TimeUnit.Month:
      return parseMonth(formattedDate);
    case TimeUnit.Quarter: {
      return parseQuarter(formattedDate, quarterStartMonth);
    }
    case TimeUnit.Year:
      return parseYear(formattedDate);
    default:
      return null;
  }
}

export function assertIsStandardTimeUnit(unit: TimeUnit): asserts unit is StandardTimeUnit {
  if (unit === TimeUnit.Session) {
    throw new Error(`${unit} is not a StandardTimeUnit`);
  }
}

/**
 * Returns the number of months between two dates.
 * The return is precise, meaning that you can get a fractional answer.
 */
export function monthDifferenceBetweenDates(laterDate: string, earlierDate: string) {
  const mLaterDate = moment(laterDate);
  const mEarlierDate = moment(earlierDate);
  return mLaterDate.diff(mEarlierDate, `months`, true);
}
export const NUMBER_OF_DAYS_IN_UNIT: {
  [key in StandardTimeUnit]: number;
} = {
  [TimeUnit.Second]: 1 / 86400,
  [TimeUnit.Minute]: 1 / 1440,
  [TimeUnit.Hour]: 1 / 24,
  [TimeUnit.Day]: 1,
  [TimeUnit.Week]: 7,
  [TimeUnit.Month]: 30,
  [TimeUnit.Quarter]: 90,
  [TimeUnit.Year]: 365,
};
